import type { Doc } from "building-types/doc";
import { docEntityTypeMap } from "building-types/doc/docEntityTypes";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import { isBuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { LabelableEntityBase } from "building-types/entities/labelTypes";
import { entitiesEligibleForEntitySettings } from "building-types/entitySettings/entitySettingsTypes";
import type {
  DocEntityHandle,
  EdgeHandle,
  GroupableEntityHandle,
  RoomEdgeHandle,
  RoomHandle,
  ShapeBasedEntityHandle,
  ShapeComponentHandle,
  ShapeHandle,
  VertexHandle,
  ViewOverrideHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { EntitySettingsEligibleHandle } from "building-types/handles/entitySettingsEligibleHandle";
import type {
  GeneratedBuildingEntityHandle,
  GeneratedEntityHandle,
} from "building-types/handles/generatedEntityHandleTypes";
import type { LabelableDocEntityType } from "building-types/handles/labelableEntityHandle";
import { LabelableDocEntityTypes, type LabelableEntityHandle } from "building-types/handles/labelableEntityHandle";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import { assertIsExactly, assertIsNotUndefined, defaultAssert } from "common/utils/assertOfType";
import { docEntityHandleHash } from "./core/handleHash";
import { isEligibleParentForEntityType } from "./docEntityConstraintTools";
import { docEntityTypeIsGroupable, type GroupableEntity } from "./docEntityGroupable";
import type { DocEntityTypeToHandleType } from "./docEntityTypeToHandleType";
import type { DocEntityTypeToParentHandleType } from "./docEntityTypeToParentHandleType";
import { docGetTableEntity } from "./docGetTableEntity";

export function isRoomEdgeHandle(handle: RoomHandle | RoomEdgeHandle | undefined): handle is RoomEdgeHandle {
  return handle?.type === "EDGE";
}

export function isShapeHandle(handle: DocEntityHandle): handle is ShapeHandle {
  return handle.type === "SHAPE";
}

export function isVertexHandle(handle: DocEntityHandle): handle is VertexHandle {
  return handle.type === "VERTEX";
}

export function isEdgeHandle(handle: DocEntityHandle): handle is EdgeHandle {
  return handle.type === "EDGE";
}

export function isShapeComponentHandle(handle: DocEntityHandle): handle is ShapeComponentHandle {
  return handle.type === "EDGE" || handle.type === "VERTEX";
}

export function isShapeOrShapeComponentHandle(handle: DocEntityHandle): handle is ShapeComponentHandle | ShapeHandle {
  return isShapeHandle(handle) || isShapeComponentHandle(handle);
}

/**
 * Determines if an entity is shape-based. Note that this doesn't mean the entity necessarily
 * has an editable shape. You should use docEntityHasEditableShape for that.
 */
export function docEntityIsShapeBased(x: DocEntityHandle): x is ShapeBasedEntityHandle {
  return (
    x.type === "ROOM" ||
    x.type === "ROOF" ||
    x.type === "REFERENCE_GEOM" ||
    x.type === "ANNOTATION" ||
    x.type === "STAIR_BLOCK" ||
    x.type === "SITE_SHAPE" ||
    x.type === "RAILING" ||
    x.type === "CEILING" ||
    x.type === "FLOOR_FINISH" ||
    x.type === "VIEWPORT" ||
    x.type === "EDIT_MODE" ||
    x.type === "FLOOR" ||
    x.type === "FLOOR_BAY" ||
    x.type === "FLOOR_SPLIT_LINE" ||
    x.type === "FOUNDATION"
  );
}

export function docEntityHandleIsGeneratedEntity(handle?: DocEntityHandle): handle is GeneratedEntityHandle {
  return handle?.type === "GENERATED_ENTITY";
}

export function docEntityHandleIsGeneratedBuildingEntityHandle(
  handle: DocEntityHandle,
): handle is GeneratedBuildingEntityHandle {
  return handle.type === "GENERATED_ENTITY" && isBuildingEntityHandle(handle.entity);
}

export function docEntityHandleEquals(a: DocEntityHandle | undefined, b: DocEntityHandle | undefined): boolean {
  if (!a || !b) {
    return false;
  }

  return docEntityHandleHash(a) === docEntityHandleHash(b);
}

export function docEntityHandleArrayEquals(a: readonly DocEntityHandle[], b: readonly DocEntityHandle[]): boolean {
  if (a === b) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }
  return a.every((handle, index) => docEntityHandleEquals(handle, b[index]));
}

export function isAnnotationParentHandle(
  parentHandle: DocEntityHandle | undefined,
): parentHandle is AnnotationParentHandle {
  return !!parentHandle && isEligibleParentForEntityType(parentHandle.type, "ANNOTATION");
}

export function isValidAnnotationCmdParentHandle(
  parentHandle: DocEntityHandle | undefined,
): parentHandle is AnnotationParentHandle {
  if (!parentHandle) {
    return false;
  }
  return isAnnotationParentHandle(parentHandle);
}

export type AnnotationParentHandle = DocEntityTypeToParentHandleType["ANNOTATION"];

export function docEntityTypeIsLabelable(type: DocEntityType): type is LabelableDocEntityType {
  return LabelableDocEntityTypes.includes(type as LabelableDocEntityType);
}

export function docEntityHandleIsLabelable(handle: DocEntityHandle): handle is LabelableEntityHandle {
  return LabelableDocEntityTypes.includes(handle.type as LabelableDocEntityType);
}

export function docEntityIsLabelable<T extends DocEntityBase>(entity: T): entity is T & LabelableEntityBase {
  return LabelableDocEntityTypes.includes(entity.entityType as LabelableDocEntityType);
}

export function isEntitySettingsEligibleHandle(
  doc: Doc,
  handle: DocEntityHandle,
): handle is EntitySettingsEligibleHandle {
  if (entitiesEligibleForEntitySettings[handle.type]) {
    if (handle.type === "ROOM") {
      const room = docGetTableEntity(doc, handle);
      return room?.type === RoomType.FRONT_PORCH || room?.type === RoomType.REAR_PORCH;
    }
    return true;
  }
  return false;
}

export function assertIsDocEntityHandleOfType<T extends DocEntityType>(
  handle: DocEntityHandle | undefined,
  type: T,
): asserts handle is DocEntityTypeToHandleType[T] {
  assertIsNotUndefined(handle);
  assertIsExactly(handle.type, type);
}

export function assertIsNotDocEntityHandleOfType<T extends DocEntityType>(
  handle: DocEntityHandle | undefined,
  type: T,
): asserts handle is Exclude<DocEntityTypeToHandleType[T], DocEntityTypeToHandleType[T]> {
  assertIsNotUndefined(handle);
  defaultAssert(handle.type !== type, `Expected handle to be of type ${type}, but got ${handle.type}`);
}

export function ensureDocEntityHandleType<T extends DocEntityType>(
  handle: DocEntityHandle | undefined,
  type: T,
): DocEntityTypeToHandleType[T] {
  assertIsDocEntityHandleOfType(handle, type);
  return handle;
}

export function assertIsDocEntityHandleArrayOfType<T extends DocEntityType>(
  handles: DocEntityHandle[],
  type: T,
): asserts handles is DocEntityTypeToHandleType[T][] {
  for (const handle of handles) {
    assertIsDocEntityHandleOfType(handle, type);
  }
}

export default function docGroupableEntityToDocEntityHandle(
  entity: GroupableEntity,
): GroupableEntityHandle | undefined {
  if (!docEntityTypeIsGroupable(entity?.entityType)) {
    return undefined;
  }

  return { type: entity.entityType, id: entity.id } as GroupableEntityHandle;
}

export function isViewOverrideHandle(handle: DocEntityHandle): handle is ViewOverrideHandle {
  return handle.type === "VIEW_OVERRIDE";
}

export function isContentTemplateHandle(handle: DocEntityHandle): boolean {
  switch (handle.type) {
    case "CONTENT_TEMPLATE":
    case "CONTENT_TEMPLATE_INSTANCE":
      return true;
    default:
      return false;
  }
}

export function isDocEntityHandle(handle: any): handle is DocEntityHandle {
  return handle && handle.type && docEntityTypeMap[handle.type] !== undefined;
}

/** Filter handles by type, narrowing the result to that handle variant. */
export function docEntityHandlesOfType<T extends DocEntityHandle["type"]>(
  handles: readonly DocEntityHandle[],
  type: T,
): Extract<DocEntityHandle, { type: T }>[] {
  return handles.filter((handle): handle is Extract<DocEntityHandle, { type: T }> => handle.type === type);
}
